// Shop-relative navigation helpers (all shop pages live under /s/:slug).
import { useCallback } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router';
import { useShop } from './boot.tsx';

/** Line icons (24×24 grid), same style as the LINE menu picture. */
const ICONS = {
  calendar: 'M4 6.5h16v13.5H4z M4 10.5h16 M8.5 4v4 M15.5 4v4 M9 15.2l2 2 4-4',
  list: 'M6 4h12v16H6z M9 9h6 M9 12.5h6 M9 16h4',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 11v5.5 M12 7.6v.2',
  user: 'M16 8a4 4 0 1 1-8 0 4 4 0 0 1 8 0z M4.5 20.5a7.5 7.5 0 0 1 15 0',
  swap: 'M7 7h12l-3-3 M17 17H5l3 3',
  plus: 'M12 5v14 M5 12h14',
  people: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z M2.5 20a6.5 6.5 0 0 1 13 0 M16 4.3a3.5 3.5 0 0 1 0 6.4 M18 14.2a6.5 6.5 0 0 1 3.5 5.8',
  sliders: 'M4 7h9 M17 7h3 M4 17h3 M11 17h9 M15 5v4 M9 15v4',
} as const;

function Icon({ name }: { name: keyof typeof ICONS }) {
  return (
    <svg className="tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICONS[name]} />
    </svg>
  );
}

export function useShopPaths() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const base = `/s/${slug}`;
  const href = useCallback((path: string) => `${base}${path.startsWith('/') ? path : `/${path}`}`, [base]);
  const go = useCallback((path: string, opts?: { replace?: boolean }) => navigate(href(path), opts), [navigate, href]);
  return { base, href, go };
}

export function CustomerTabs() {
  const { href } = useShopPaths();
  const { isStaff } = useShop();
  const tab = (path: string, icon: keyof typeof ICONS, label: string) => (
    <NavLink to={href(path)} className={({ isActive }) => (isActive ? 'active' : '')}>
      <Icon name={icon} />{label}
    </NavLink>
  );
  return (
    <nav className="tabbar" aria-label="เมนูลูกค้า">
      {tab('/book', 'calendar', 'จองคิว')}
      {tab('/my', 'list', 'การจองของฉัน')}
      {tab('/info', 'info', 'ข้อมูลร้าน')}
      {isStaff ? tab('/staff/today', 'swap', 'โหมดพนักงาน') : tab('/profile', 'user', 'โปรไฟล์')}
    </nav>
  );
}

export function StaffTabs({ owner }: { owner: boolean }) {
  const { href } = useShopPaths();
  const tab = (path: string, icon: keyof typeof ICONS, label: string) => (
    <NavLink to={href(path)} className={({ isActive }) => (isActive ? 'active' : '')}>
      <Icon name={icon} />{label}
    </NavLink>
  );
  return (
    <nav className="tabbar" aria-label="เมนูพนักงาน">
      {tab('/staff/today', 'list', 'คิววันนี้')}
      {tab('/staff/calendar', 'calendar', 'ปฏิทิน')}
      {tab('/staff/book', 'plus', 'จองให้ลูกค้า')}
      {tab('/staff/customers', 'people', 'ลูกค้า')}
      {owner && tab('/staff/manage', 'sliders', 'จัดการร้าน')}
    </nav>
  );
}
